import debug from 'debug';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { merge } from 'ts-deepmerge';
import { languageByCountryCode } from '@/config/constants';
import { welcomeScreenSelector, widgetIdSelector, userConfigState, } from '@/state/config';
import { chatEventCountSelector, hideWebchatState, isMobileState, lastActivityInPageState, sessionIdSelector, } from '@/state/session';
import { connectionState } from '@/state/socket';
import { WebClientEventType } from '@/ts';
import { userWayConfig } from '@/util/userway';
import { getTimestamp, parseEvent } from '@/util/utils';
import { useController } from './useController';
import { useSocket } from './useSocket';
import { useWidgetState } from './useWidgetState';
export const useIframeController = () => {
    const { i18n } = useTranslation();
    const widgetId = useRecoilValue(widgetIdSelector);
    const setIsMobile = useSetRecoilState(isMobileState);
    const { updateContext, sendMessage, closeSession } = useSocket();
    const setLastActivityInPageState = useSetRecoilState(lastActivityInPageState);
    const { closeWidget, openWidget } = useWidgetState();
    const chatEventsCount = useRecoilValue(chatEventCountSelector);
    const setUserConfig = useSetRecoilState(userConfigState);
    const welcomeScreen = useRecoilValue(welcomeScreenSelector);
    const sessionId = useRecoilValue(sessionIdSelector);
    const setHideWebchatState = useSetRecoilState(hideWebchatState);
    const log = debug(`moveo:iframe:${widgetId}:controller`);
    const socketConnectionState = useRecoilValue(connectionState);
    const [contextQueue, setContextQueue] = useState(null);
    const { notifyLocaleChanged, resizeWidget } = useController();
    useEffect(() => {
        if (contextQueue && socketConnectionState === 'online' && sessionId) {
            updateContext(contextQueue);
            setContextQueue(null);
        }
    }, [contextQueue, updateContext, socketConnectionState, sessionId]);
    const handleEvents = useCallback((messageEvent) => {
        var _a, _b;
        const event = parseEvent(widgetId, messageEvent);
        if (!event) {
            return;
        }
        log(`Event from web-client: ${event.type}`, event);
        switch (event.type) {
            case WebClientEventType.Open: {
                openWidget();
                break;
            }
            case WebClientEventType.Close: {
                closeWidget();
                break;
            }
            case WebClientEventType.WindowResize: {
                setIsMobile(event.data.is_mobile);
                resizeWidget(event.data.is_mobile);
                break;
            }
            case WebClientEventType.SetLocale: {
                log(`Set locale to ${event.data}`);
                if (Object.keys(languageByCountryCode).includes(event.data)) {
                    i18n.changeLanguage(event.data, (err) => {
                        if (!err) {
                            notifyLocaleChanged(event.data);
                        }
                    });
                }
                break;
            }
            case WebClientEventType.UpdateContext: {
                log('Update context with:', event.data);
                setContextQueue((prev) => prev ? merge(prev, event.data) : event.data);
                break;
            }
            case WebClientEventType.SendMessage: {
                if (chatEventsCount > 0) {
                    sendMessage(event.data);
                }
                else if (welcomeScreen === null || welcomeScreen === void 0 ? void 0 : welcomeScreen.enabled) {
                    setUserConfig((prev) => merge({}, prev, {
                        welcome_trigger_message: event.data.text,
                        welcome_screen: Object.assign(Object.assign({}, welcomeScreen), { enabled: false }),
                    }));
                }
                else {
                    setUserConfig((prev) => { var _a; return merge({}, prev, { welcome_trigger_message: (_a = event.data) === null || _a === void 0 ? void 0 : _a.text }); });
                }
                break;
            }
            case WebClientEventType.SetCssVariables: {
                const cssVariables = event.data;
                const container = window.document.querySelector('.appContainer');
                for (const key in cssVariables) {
                    container === null || container === void 0 ? void 0 : container.style.setProperty(key, cssVariables[key]);
                }
                break;
            }
            case WebClientEventType.SessionActivity: {
                if (event.data === 'active') {
                    setLastActivityInPageState(getTimestamp());
                }
                break;
            }
            //Userway functionality
            case WebClientEventType.UserWayDisabled: {
                const { eventType } = event.data;
                const disabledFunction = new Function((_a = userWayConfig[eventType]) === null || _a === void 0 ? void 0 : _a.disabled);
                disabledFunction();
                break;
            }
            case WebClientEventType.UserWayEnabled: {
                const { eventType, value } = event.data;
                if (window.UserWay && value) {
                    const enabledFunction = new Function('value', (_b = userWayConfig[eventType]) === null || _b === void 0 ? void 0 : _b.enabled);
                    enabledFunction(value);
                }
                break;
            }
            case WebClientEventType.CloseConversation: {
                closeSession();
                setHideWebchatState(true);
            }
            default: {
                log(`Unhandle event: ${event.type}`);
            }
        }
    }, [
        widgetId,
        log,
        openWidget,
        closeWidget,
        setIsMobile,
        resizeWidget,
        i18n,
        notifyLocaleChanged,
        chatEventsCount,
        welcomeScreen,
        sendMessage,
        setUserConfig,
        setLastActivityInPageState,
        closeSession,
        setHideWebchatState,
    ]);
    // Listen to events from the web-client's controller (customer's page)
    useEffect(() => {
        window.addEventListener('message', handleEvents);
        return () => {
            window.removeEventListener('message', handleEvents);
        };
    }, [handleEvents]);
};
